import React, { useEffect, useState, useRef } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { springConfig, useCountUp } from '../utils/animations';

interface StatDisplayProps {
  icon?: React.ElementType;
  value: string | number;
  label: string;
  color?: string;
  animated?: boolean;
  className?: string;
}

// Extract numeric value from string for animation
const extractNumber = (value: string | number): { num: number; prefix: string; suffix: string } => {
  if (typeof value === 'number') {
    return { num: value, prefix: '', suffix: '' };
  }

  // Match patterns like "1,234", "12.5%", "1.23B", "123 / 456"
  const match = value.match(/^([^\d]*)([\d,]+\.?\d*)(.*)$/);
  if (match) {
    const numStr = match[2].replace(/,/g, '');
    return {
      prefix: match[1],
      num: parseFloat(numStr) || 0,
      suffix: match[3]
    };
  }

  return { num: 0, prefix: '', suffix: value };
};

// Format number with commas
const formatNumber = (num: number, decimals: number = 0): string => {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const AnimatedNumber: React.FC<{
  value: string | number;
  animate: boolean;
}> = ({ value, animate }) => {
  const { num, prefix, suffix } = extractNumber(value);
  const hasDecimal = String(value).includes('.');
  const decimals = hasDecimal ? (String(value).split('.')[1]?.match(/\d+/)?.[0]?.length || 2) : 0;

  const animatedValue = useCountUp(
    Math.round(num * Math.pow(10, decimals)),
    animate ? 1200 : 0,
    100
  );

  const displayValue = formatNumber(animatedValue / Math.pow(10, decimals), decimals);

  return (
    <span>
      {prefix}
      {displayValue}
      {suffix}
    </span>
  );
};

const StatDisplay: React.FC<StatDisplayProps> = ({
  icon: Icon,
  value,
  label,
  color = "blue",
  animated = false,
  className = ""
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [hasAnimated, setHasAnimated] = useState(false);
  const prevValueRef = useRef(value);

  // Track if value changed to trigger icon bounce
  const valueChanged = prevValueRef.current !== value;
  useEffect(() => {
    prevValueRef.current = value;
  }, [value]);

  // Only animate count-up on first render
  useEffect(() => {
    if (animated && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [animated, hasAnimated]);

  // Map standard colors to catppuccin colors for dark mode
  const catppuccinColorMap: Record<string, string> = {
    blue: 'blue',
    red: 'red',
    green: 'green',
    yellow: 'yellow',
    purple: 'mauve',
    orange: 'peach',
    indigo: 'lavender',
    gray: 'overlay0',
    pink: 'pink',
    cyan: 'sky'
  };

  const catppuccinColor = catppuccinColorMap[color] || color;

  // Icon animation variants
  const iconVariants = {
    initial: { scale: 1, rotate: 0 },
    bounce: {
      scale: [1, 1.2, 1],
      rotate: [0, -10, 10, 0],
      transition: { duration: 0.4 }
    },
    float: {
      y: [0, -3, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut' as const
      }
    }
  };

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.gentle
    },
    hover: {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      transition: { duration: 0.2 }
    }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <div className={`
        flex items-center space-x-3 p-3 rounded-lg
        transition-colors duration-300 hover:bg-gray-50 dark:hover:bg-catppuccin-surface1
        ${className}
      `}>
        {Icon && (
          <div className={`
            p-2 rounded-full bg-${color}-100 text-${color}-500
            dark:bg-catppuccin-surface1 dark:text-catppuccin-${catppuccinColor}
          `}>
            <Icon size={20} />
          </div>
        )}
        <div>
          <div className="font-semibold text-gray-800 dark:text-catppuccin-text dark:font-bold tracking-wide">
            {value}
          </div>
          <div className="text-xs text-gray-500 dark:text-catppuccin-subtext1 dark:font-medium">
            {label}
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className={`
        flex items-center space-x-3 p-3 rounded-lg
        ${className}
      `}
      variants={containerVariants}
      initial={animated ? "hidden" : "visible"}
      animate="visible"
      whileHover="hover"
    >
      {Icon && (
        <motion.div
          className={`
            p-2 rounded-full bg-${color}-100 text-${color}-500
            dark:bg-catppuccin-surface1 dark:text-catppuccin-${catppuccinColor}
          `}
          variants={iconVariants}
          initial="initial"
          animate={animated ? "float" : (valueChanged ? "bounce" : "initial")}
        >
          <Icon size={20} />
        </motion.div>
      )}
      <div>
        <motion.div
          className="font-semibold text-gray-800 dark:text-catppuccin-text dark:font-bold tracking-wide"
          key={String(value)} // Key change triggers AnimatePresence
        >
          {animated && !hasAnimated ? (
            <AnimatedNumber value={value} animate={true} />
          ) : (
            <AnimatePresence mode="wait">
              <motion.span
                key={String(value)}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                {value}
              </motion.span>
            </AnimatePresence>
          )}
        </motion.div>
        <div className="text-xs text-gray-500 dark:text-catppuccin-subtext1 dark:font-medium">
          {label}
        </div>
      </div>
    </motion.div>
  );
};

export default StatDisplay;
